import fetcher from '@helpers/fetcher';
import { TokenManager } from './TokenManager';

// Add reusable helpers to improve readability and avoid duplication
const PIN_DATA_ENDPOINT = '/v3/users/me/pin/get-pin-data';
const REFRESH_PIN_TOKEN_ENDPOINT = '/v7/refresh/pin';

const fetchPinData = () => fetcher.get(PIN_DATA_ENDPOINT, { meta: { isUsingPinToken: true } });

const extractPinData = (response) => {
  if (!response || response.status !== 200) {
    return { isPin6Digit: null, hasSetPin: null };
  }

  const isPin6Digit = response.data.result.is_pin_6digit.toString();
  const hasSetPin = response.data.result.has_set_pin.toString();

  return { isPin6Digit, hasSetPin };
};

const persistPinCookies = ({ isPin6Digit, hasSetPin }, shouldPersistHasSetPin) => {
  if (isPin6Digit === null) return;

  TokenManager.setCookieData('is_pin_6digit', isPin6Digit);

  if (shouldPersistHasSetPin) {
    TokenManager.setCookieData('has_set_pin', hasSetPin);
  }

  const currentNeedChange = TokenManager.getPinNeedChange();

  if (isPin6Digit === 'false') {
    // Mark that user needs to upgrade PIN unless they already opted to skip
    if (currentNeedChange !== 'false') {
      TokenManager.setCookieData('pin_need_change', 'true');
    }
  } else if (isPin6Digit === 'true') {
    // User already has 6-digit PIN → ensure flag is set to false
    if (currentNeedChange !== 'false') {
      TokenManager.setCookieData('pin_need_change', 'false');
    }
  }
};

export const ApiService = {
  async fetchPinTokenAndData() {
    try {
      // Current cookie state
      const hasSetPinCookie = TokenManager.getHasSetPin();

      // 1. Try to use existing pin token first
      const existingPinToken = TokenManager.getPinToken();
      if (existingPinToken) {
        const pinData = extractPinData(await fetchPinData());
        persistPinCookies(pinData, hasSetPinCookie === undefined);
        return { pinToken: existingPinToken, ...pinData };
      }

      // 2. No existing token, attempt to refresh
      const { getRefreshPinToken } = await import('@helpers/fetcher/token');
      if (!getRefreshPinToken()) {
        return { pinToken: null, hasSetPin: null, isPin6Digit: null };
      }

      const refreshResponse = await fetcher.post(REFRESH_PIN_TOKEN_ENDPOINT, {}, false, false);
      const refreshedPinToken = refreshResponse?.data?.pin_token;

      if (!refreshedPinToken) {
        return { pinToken: null, hasSetPin: null, isPin6Digit: null };
      }

      TokenManager.setPinToken(refreshedPinToken);
      TokenManager.setRefreshPinToken(refreshResponse.data.pin_refresh);

      const pinData = extractPinData(await fetchPinData());
      persistPinCookies(pinData, hasSetPinCookie === undefined);

      return { pinToken: refreshedPinToken, ...pinData };
    } catch (err) {
      return { pinToken: null, hasSetPin: null, isPin6Digit: null };
    }
  },
};
